"use client";

import { useCallback } from "react";
import {
  formatFiatCurrency,
  formatUsdtWithLocalCurrency,
  getFiatCurrencySymbol,
  getCurrencyDecimalPlaces,
} from "@cede/utils";
import { useCurrencyStore } from "@/store/currencyStore";

export interface FormatOptions {
  /** Use compact notation (K, M, B) */
  compact?: boolean;
  /** Show USDT value in parentheses (only for non-USD) */
  showUsdt?: boolean;
  /** Override the currency code */
  currencyCode?: string;
}

/**
 * Hook that provides currency formatting functions using the store's selected currency
 */
export const useFormatCurrency = () => {
  const { selectedCurrency, rates, convertFromUsdt } = useCurrencyStore();

  /**
   * Format a USDT value in the user's selected local currency
   * For USD, just shows "$1,000.00"
   * For other currencies, shows "Rp 15,800,000 (1,000.00 USDT)"
   */
  const formatUsdt = useCallback(
    (usdtValue: number, options: FormatOptions = {}): string => {
      const { compact = false, showUsdt = true, currencyCode } = options;
      const currency = currencyCode || selectedCurrency;
      const rate = rates?.[currency.toLowerCase()] || 1;

      return formatUsdtWithLocalCurrency(usdtValue, {
        currencyCode: currency,
        rate,
        showUsdt: showUsdt && currency.toLowerCase() !== "usd",
        compact,
      });
    },
    [selectedCurrency, rates],
  );

  /**
   * Format a value in the user's local currency (no USDT conversion)
   * Use this when the value is already in local currency
   */
  const formatLocal = useCallback(
    (localValue: number, options: FormatOptions = {}): string => {
      const { compact = false, currencyCode } = options;
      const currency = currencyCode || selectedCurrency;

      return formatFiatCurrency(localValue, {
        currencyCode: currency,
        compact,
      });
    },
    [selectedCurrency],
  );

  /**
   * Format a USDT value showing ONLY the local currency (no USDT in parentheses)
   * Useful for portfolio views where everything is in local currency
   */
  const formatUsdtAsLocal = useCallback(
    (usdtValue: number, options: Omit<FormatOptions, "showUsdt"> = {}): string => {
      const { compact = false, currencyCode } = options;
      const currency = currencyCode || selectedCurrency;
      const rate = rates?.[currency.toLowerCase()] || 1;
      const localValue = usdtValue * rate;

      return formatFiatCurrency(localValue, {
        currencyCode: currency,
        compact,
      });
    },
    [selectedCurrency, rates],
  );

  /**
   * Get the currency symbol for the selected currency
   */
  const getCurrencySymbol = useCallback(
    (currencyCode?: string): string => {
      return getFiatCurrencySymbol(currencyCode || selectedCurrency);
    },
    [selectedCurrency],
  );

  /**
   * Get the decimal places for the selected currency
   */
  const getDecimalPlaces = useCallback(
    (currencyCode?: string): number => {
      return getCurrencyDecimalPlaces(currencyCode || selectedCurrency);
    },
    [selectedCurrency],
  );

  /**
   * Check if the selected currency is USD
   */
  const isUsd = selectedCurrency === "usd";

  return {
    /** Format a USDT value with local currency (shows both for non-USD) */
    formatUsdt,
    /** Format a local currency value */
    formatLocal,
    /** Format a USDT value as local only (no USDT parentheses) */
    formatUsdtAsLocal,
    /** Get the currency symbol */
    getCurrencySymbol,
    /** Get decimal places for currency */
    getDecimalPlaces,
    /** Convert USDT to local currency value */
    convertFromUsdt,
    /** Currently selected currency code */
    selectedCurrency,
    /** Whether currently using USD */
    isUsd,
    /** Current conversion rates */
    rates,
  };
};
